/** @file Writes world-space vertices of a skinned mesh (weighted bone worlds x positions + deform). */
import { POSE_LAYOUT } from '@pose2d/core/contracts/pose';
import { invariant } from '@pose2d/std/invariant';
import type { MeshAttachmentData } from '@pose2d/core/types';

const WS = POSE_LAYOUT.WORLD_STRIDE;

/**
 * Writes 2 floats per vertex into out at offset; returns the new offset. Each vertex is Σ wᵢ · Wᵢ · (pᵢ + δᵢ) over
 * its `counts[v]` influences (spec §6.7 compiled form): pᵢ bone-local, δᵢ the per-influence deform (null = none),
 * Wᵢ the bone's block in `world` (the whole Pose.world). Throws (invariant) for a rigid mesh.
 */
export function skinVertices(out: Float32Array, offset: number, att: MeshAttachmentData, world: Float64Array, deform: Float64Array | null): number {
  const skin = att.skin;
  invariant(skin.kind === 'skinned', 'skinVertices needs a skinned mesh (rigid meshes go through meshVertices)');
  const { counts, bones, positions, weights } = skin;
  let inf = 0;
  let o = offset;
  for (let v = 0; v < counts.length; v++) {
    const end = inf + (counts[v] ?? 0);
    let x = 0;
    let y = 0;
    for (; inf < end; inf++) {
      const wo = (bones[inf] ?? 0) * WS, w = weights[inf] ?? 0;
      const px = (positions[2 * inf] ?? 0) + (deform === null ? 0 : (deform[2 * inf] ?? 0));
      const py = (positions[2 * inf + 1] ?? 0) + (deform === null ? 0 : (deform[2 * inf + 1] ?? 0));
      x += w * ((world[wo] ?? 0) * px + (world[wo + 1] ?? 0) * py + (world[wo + 4] ?? 0));
      y += w * ((world[wo + 2] ?? 0) * px + (world[wo + 3] ?? 0) * py + (world[wo + 5] ?? 0));
    }
    out[o] = x;
    out[o + 1] = y;
    o += 2;
  }
  return o;
}
